import { useState, useEffect } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useSessionAuth } from "@/app/_utils/sessionClient";
import { PERMISSIONS } from "@/app/_utils/permissions";
import { Merch } from "@/typings";
import MerchService from "@/app/_services/MerchService";
import PageContainer from "@/app/admin/_common/PageContainer";
import MerchDetailHeader from "./_cmp/MerchDetailHeader";

const TAB_PERMISSIONS: Record<string, string> = {
  details: PERMISSIONS.PAGE_MERCH,
  orders: PERMISSIONS.PAGE_ORDERS,
  settings: PERMISSIONS.MERCH_WRITE,
};

const INVALID_PRODUCT_IDS = new Set(["undefined", "null"]);

export default function MerchDashLayout() {
  const { product_id } = useParams<{ product_id: string }>();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { session, can } = useSessionAuth();
  // The API returns one row per variant, so this is an array — `merch[0]` holds
  // the product-level fields shared by every variant.
  const [merch, setMerch] = useState<Merch[]>();
  const isValidProductId = Boolean(product_id) && !INVALID_PRODUCT_IDS.has(product_id ?? "");

  useEffect(() => {
    if (!isValidProductId) {
      navigate("/admin/merch", { replace: true });
      return;
    }
    MerchService.getMerchAsync(product_id!).then(setMerch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product_id, isValidProductId]);

  useEffect(() => {
    if (!session || !product_id) return;
    const currentTab = pathname.split("/").slice(-1)[0];
    const needed = TAB_PERMISSIONS[currentTab];
    if (needed && !can(needed)) {
      navigate("/admin/merch", { replace: true });
    }
  }, [pathname, session, can, product_id, navigate]);

  return (
    <PageContainer flush>
      <div className="flex h-screen max-h-screen w-full flex-col overflow-hidden">
        {merch && (
          <MerchDetailHeader
            merch={merch}
            productId={product_id}
          />
        )}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-5 md:px-5">
          {merch && <Outlet />}
        </div>
      </div>
    </PageContainer>
  );
}
